import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { X, Calendar } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { LeaveType } from '@/types';
import { leaveService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

interface LeaveRequestModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * LeaveRequestModal
 * Form for requesting time off / leave
 */
const LeaveRequestModal: React.FC<LeaveRequestModalProps> = ({ open, onClose }) => {
  const user = useAppSelector(selectCurrentUser);
  const queryClient = useQueryClient();
  
  const [leaveType, setLeaveType] = useState<LeaveType>(LeaveType.CASUAL);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(1, 'day'));
  const [reason, setReason] = useState('');

  const createLeaveMutation = useMutation({
    mutationFn: (data: { type: LeaveType; startDate: string; endDate: string; reason: string; delegateTo?: string }) =>
      leaveService.createLeaveRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myLeaves });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calendarEvents });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.pendingApprovals] });
      handleClose();
    },
  });

  const handleSubmit = async () => {
    if (!user || !startDate || !endDate) return;
    createLeaveMutation.mutate({
      type: leaveType,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      reason: reason.trim(),
    });
  };

  const handleClose = () => {
    setLeaveType(LeaveType.CASUAL);
    setStartDate(dayjs());
    setEndDate(dayjs().add(1, 'day'));
    setReason('');
    onClose();
  };

  const leaveTypes = [
    { value: LeaveType.CASUAL, label: 'Casual Leave' },
    { value: LeaveType.SICK, label: 'Sick Leave' },
    { value: LeaveType.EARNED, label: 'Earned Leave' },
    { value: LeaveType.UNPAID, label: 'Unpaid Leave' },
    { value: LeaveType.COMPENSATORY, label: 'Compensatory Off' },
  ];

  const totalDays = startDate && endDate ? endDate.diff(startDate, 'day') + 1 : 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Calendar size={20} />
              <Typography variant="h6" fontWeight={600}>
                Request Leave
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          {createLeaveMutation.isSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Leave request submitted successfully! Your manager will review it shortly.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              {/* Leave Type */}
              <FormControl fullWidth>
                <InputLabel>Leave Type</InputLabel>
                <Select
                  value={leaveType}
                  label="Leave Type"
                  onChange={(e) => setLeaveType(e.target.value as LeaveType)}
                >
                  {leaveTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Date Range */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{
                    textField: { fullWidth: true, size: 'medium' },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate || undefined}
                  slotProps={{
                    textField: { fullWidth: true, size: 'medium' },
                  }}
                />
              </Box>

              {/* Reason */}
              <TextField
                label="Reason (Optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                rows={3}
                fullWidth
                placeholder="Briefly describe the reason for your leave request..."
              />

              {/* Info */}
              <Alert severity="info">
                Your request will be sent to your direct manager for approval.
                You'll be notified once a decision is made.
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={createLeaveMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createLeaveMutation.isPending || createLeaveMutation.isSuccess || !startDate || !endDate}
          >
            {createLeaveMutation.isPending ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default LeaveRequestModal;
